import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import PlacowkaService from '@/services/PlacowkaService'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import TrainingForm from '../common/TrainingForm'
import TrainingPlacowkaSection from '../common/TrainingPlacowkaSection'
import { buildTrainingPayload } from '../common/submitTraining'
import useTrainingAddPage from './hooks/useTrainingAddPage'

export default function TrainingAdd() {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const presetPlacowkaId = searchParams.get('id_placowka')
	const [selectedPlacowka, setSelectedPlacowka] = useState(null)
	const {
		certificationOptions,
		handleCancel,
		handleSubmit,
		loading,
		submitting,
		trainingNameOptions,
		contentOptions,
	} = useTrainingAddPage()

	useEffect(() => {
		if (!presetPlacowkaId) return undefined

		let alive = true

		;(async () => {
			const result = await PlacowkaService.getById(presetPlacowkaId)
			if (!alive || result?.error || !result?.data) return
			setSelectedPlacowka(result.data)
		})()

		return () => {
			alive = false
		}
	}, [presetPlacowkaId])

	const handleClearPlacowka = useCallback(() => {
		setSelectedPlacowka(null)
	}, [])

	const onSubmit = useCallback(async form => {
		if (!selectedPlacowka) {
			await Swal.fire({
				icon: 'error',
				title: t('trainings.add.messages.errorTitle'),
				text: t('trainings.add.messages.placowkaRequired'),
			})
			return
		}

		await handleSubmit(buildTrainingPayload(form, selectedPlacowka))
	}, [handleSubmit, selectedPlacowka, t])

	if (loading) {
		return <LoadingIndicator message={t('trainings.add.loading')} />
	}

	return (
		<div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">
			<div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h1 className="text-2xl font-bold text-gray-900">{t('trainings.add.title')}</h1>
				</div>

				<div className="px-4 sm:px-6 py-6">
					<div className="space-y-6">
						<TrainingPlacowkaSection
							selectedPlacowka={selectedPlacowka}
							onSelectPlacowka={setSelectedPlacowka}
							onClearPlacowka={handleClearPlacowka}
							t={t}
						/>

						<TrainingForm
							mode="add"
							certificationOptions={certificationOptions}
							trainingNameOptions={trainingNameOptions}
							contentOptions={contentOptions}
							onSubmit={onSubmit}
							onCancel={handleCancel}
							submitting={submitting}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}
